import { useCallback, useState } from 'react';

/**
 * Soft-delete + undo flow shared by list row actions, bulk delete, and the
 * detail page's delete button. `onDelete`/`onRestore` receive a single id.
 *
 * `remove(idOrIds, message)` deletes immediately (backend soft-deletes) and
 * opens the undo snackbar; `undo()` restores every id from that batch;
 * `dismiss()` just clears the snackbar without restoring anything.
 */
export default function useUndoDelete({ onDelete, onRestore }) {
  const [pending, setPending] = useState(null);

  const remove = useCallback(
    async (idOrIds, message) => {
      const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
      await Promise.all(ids.map((id) => onDelete(id)));
      setPending({ ids, message });
    },
    [onDelete],
  );

  const undo = useCallback(async () => {
    if (!pending) return;
    await Promise.all(pending.ids.map((id) => onRestore(id)));
    setPending(null);
  }, [pending, onRestore]);

  const dismiss = useCallback(() => setPending(null), []);

  /**
   * Opens the undo snackbar for a deletion that already happened elsewhere
   * (e.g. the detail page deletes then navigates back to the list).
   */
  const seed = useCallback((idOrIds, message) => {
    const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
    setPending({ ids, message });
  }, []);

  return { pending, remove, undo, dismiss, seed };
}
